/**
 * Competitors page helpers (pages/Competitors.tsx). Pure: no imports and only erasable types,
 * so node --test loads this file directly (test/competitors.test.js). The server's copies of
 * funnelKind, displayCopy, cleanAdUrl, landingKey and daysRunning live in utils/competitors.js;
 * the test keeps the two in step.
 *
 * The wording rules ported from PyreOS:
 *  - A blocked or errored scan is "Could not check", never a zero and never "no ads".
 *    Only a clean empty scan may say "No active ads".
 *  - "Running longest" is how long an ad has been live. Meta shares no results for ordinary
 *    advertisers, so it is never called "best performing".
 *  - Every number is among the ads we read.
 */

export type ScanStatus = "ok" | "empty" | "blocked" | "error";
export type FunnelKind = "website" | "lead_form" | "messenger" | "call";

export type CompetitorStats = {
  as_of: string;
  /** Meta's own count of active ads when the page was read by id, else the ads we read. */
  active: number;
  active_from_meta: boolean;
  scanned: number;
  new_7d: number;
  longest_days: number | null;
  video: number;
  image: number;
  sends: Record<FunnelKind, number>;
  top_landing: { label: string; url: string; count: number } | null;
};

export type Competitor = {
  id: number;
  client_id: number;
  kind: "competitor" | "self";
  label: string;
  query: string;
  page_id: string | null;
  page_name: string | null;
  page_url: string | null;
  country: string;
  level: number | null;
  watching: boolean;
  last_scanned_at: string | null;
  last_scan_status: ScanStatus | null;
  last_scan_detail: string | null;
  reported_total: number | null;
  page_likes: number | null;
  scanning: boolean;
  cached_ads: number;
  ad_library_url: string;
  stats: CompetitorStats | null;
  instagram_handle?: string | null;
  instagram_followers?: number | null;
  social_status?: ScanStatus | null;
  social_detail?: string | null;
  social_synced_at?: string | null;
  /** Their posts (utils/competitorSocial.js socialStats). */
  social?: { posts: number; posts_30d: number; typical_reel_views: number | null; outliers: number; latest_post_at: string | null } | null;
};

export type ScanProvider = { id: "scrapecreators" | "apify"; label: string; per_scan_text: string };
export type ScanCost = {
  provider: ScanProvider["id"] | null;
  /** "1 to 2 credits" (Scrape Creators) or "about $0.05" (Apify). */
  per_scan_text: string | null;
  per_scan_usd: number;
  per_1000_ads_usd: number;
  ads_per_scan: number;
  rescan_days: number;
  /** Scrape Creators credits left, when it is the provider and answered. */
  credits_left: number | null;
};

export type CompetitorsResponse = {
  client: { id: number; name: string };
  /** Scrape Creators is used when its key is saved, else Apify; null when neither is. */
  ready: boolean;
  provider: ScanProvider | null;
  can_change: boolean;
  cost: ScanCost;
  competitors: Competitor[];
  /** "Find competitors" starts from the client's industry and first location. */
  find_defaults?: { service: string; area: string };
};

export type CompetitorAd = {
  id: number;
  ad_archive_id: string;
  page_name: string | null;
  headline: string | null;
  primary_text: string | null;
  cta: string | null;
  cta_type: string | null;
  link_url: string | null;
  link_description: string | null;
  media_type: string | null;
  image_url: string | null;
  video_url: string | null;
  preview_image_url: string | null;
  platforms: string[];
  started_running_at: string | null;
  first_seen_at: string;
  last_seen_at: string;
  active: boolean;
  ad_library_url: string;
};

export type AdsResponse = {
  ads: CompetitorAd[];
  active_since: string | null;
  last_good_status: ScanStatus | null;
  counts: { total: number; active: number };
};

export type ScanRow = {
  id: number;
  status: ScanStatus;
  ads_found: number;
  reported_total: number | null;
  provider: "apify" | "scrapecreators";
  cost_usd: string | number | null;
  credits_used: number | null;
  triggered_by: "manual" | "cron";
  detail: string | null;
  started_at: string;
  finished_at: string | null;
  by_name: string | null;
};

/* ── levels ─────────────────────────────────────────────────────────────── */

/** How close a competitor is to the client. Null is "Not ranked", never a guess. */
export const LEVELS = [1, 2, 3, 4] as const;
export const LEVEL_META: Record<number, { name: string; short: string }> = {
  1: { name: "Direct competitor", short: "Same offer, same customers" },
  2: { name: "Same offer, other customers", short: "Same offer, other customers" },
  3: { name: "Other offer, same customers", short: "Other offer, same customers" },
  4: { name: "Big name to learn from", short: "Other offer, other customers" },
};

export function levelLabel(level: number | null | undefined): string {
  return level && LEVEL_META[level] ? `Level ${level}: ${LEVEL_META[level].short}` : "Not ranked";
}

/* ── status wording ─────────────────────────────────────────────────────── */

export type CheckState = { label: string; tone: "neutral" | "success" | "warning"; unknown: boolean };

/** What the Last checked column says. blocked and error are "Could not check", never a count. */
export function checkState(status: ScanStatus | null | undefined): CheckState {
  if (status === "blocked" || status === "error") return { label: "Could not check", tone: "warning", unknown: true };
  if (status === "empty") return { label: "No active ads", tone: "neutral", unknown: false };
  if (status === "ok") return { label: "Checked", tone: "success", unknown: false };
  return { label: "Never checked", tone: "neutral", unknown: true };
}

/* ── reading an ad ──────────────────────────────────────────────────────── */

const PLACEHOLDER = /\{\{[^{}]*\}\}/g;

/** Does this text carry a placeholder Meta fills in per product ({{product.name}})? */
export function isTemplated(value: string | null | undefined): boolean {
  return typeof value === "string" && /\{\{[^{}]*\}\}/.test(value);
}

/** Real copy or null: a placeholder alone is no copy; real words around one are kept. */
export function displayCopy(value: string | null | undefined): string | null {
  if (!value) return null;
  const stripped = String(value).replace(PLACEHOLDER, "").replace(/\s{2,}/g, " ").trim();
  return stripped.length ? stripped : null;
}

/** A destination with Meta's delivery-time macros removed, safe to click, or null. */
export function cleanAdUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const stripped = String(url).replace(PLACEHOLDER, "").trim();
  return /^https?:\/\//i.test(stripped) ? stripped : null;
}

/** "1800gotjunk.com/us_en/hello-relief": host and path, no query string. */
export function landingKey(url: string | null | undefined): string | null {
  const clean = cleanAdUrl(url);
  if (!clean) return null;
  try {
    const u = new URL(clean);
    return `${u.hostname.replace(/^www\./i, "").toLowerCase()}${u.pathname.replace(/\/+$/, "")}`;
  } catch {
    return null;
  }
}

/** Where an ad sends people. No link is meaningful: a lead form or message ad stays on Meta. */
export function funnelKind(linkUrl: string | null | undefined, ctaType?: string | null): FunnelKind {
  const link = String(linkUrl || "").trim();
  const cta = String(ctaType || "");
  if (/MESSAGE|WHATSAPP|MESSENGER/i.test(cta) || /^https?:\/\/(www\.)?(m\.me|wa\.me|api\.whatsapp\.com|messenger\.com)\//i.test(link)) return "messenger";
  if (/^CALL/i.test(cta) || /^tel:/i.test(link)) return "call";
  if (!/^https?:\/\//i.test(link) || /^https?:\/\/(www\.|l\.|m\.)?(fb\.me|facebook\.com|fb\.com|instagram\.com)(\/|$)/i.test(link)) return "lead_form";
  return "website";
}

export const FUNNEL_LABEL: Record<FunnelKind, string> = { website: "Website", lead_form: "Lead form", messenger: "Messenger", call: "Phone call" };

/** The kinds an advertiser's ads send people to, most used first, zeros left out. */
export function sendsLines(sends: Partial<Record<FunnelKind, number>> | null | undefined): { kind: FunnelKind; label: string; count: number }[] {
  const order: FunnelKind[] = ["website", "lead_form", "messenger", "call"];
  return order
    .map((kind) => ({ kind, label: FUNNEL_LABEL[kind], count: Number(sends?.[kind] || 0) }))
    .filter((l) => l.count > 0)
    .sort((a, b) => b.count - a.count);
}

/** Whole days since the ad started. Null when unknown, which is not zero. */
export function daysRunning(startedRunningAt: string | null | undefined, now: Date = new Date()): number | null {
  if (!startedRunningAt) return null;
  const started = new Date(startedRunningAt);
  if (Number.isNaN(started.getTime())) return null;
  const days = Math.floor((now.getTime() - started.getTime()) / 86400000);
  return days < 0 ? 0 : days;
}

export function daysText(days: number | null | undefined): string {
  if (days === null || days === undefined) return "—";
  return days === 1 ? "1 day" : `${days.toLocaleString("en-US")} days`;
}

/** Meta's permalink for one ad: it never expires, unlike the signed creative URLs. */
export function adPermalink(adArchiveId: string | null | undefined): string | null {
  const id = String(adArchiveId || "").trim();
  return id ? `https://www.facebook.com/ads/library/?id=${encodeURIComponent(id)}` : null;
}

const PLATFORM_NAMES: Record<string, string> = {
  facebook: "Facebook", instagram: "Instagram", messenger: "Messenger", audience_network: "Audience Network", threads: "Threads", whatsapp: "WhatsApp",
};

/** "Facebook, Instagram, Messenger" in a stable order. */
export function platformsLabel(platforms: string[] | null | undefined): string {
  const order = Object.keys(PLATFORM_NAMES);
  const list = [...new Set((platforms || []).map((p) => String(p).toLowerCase()))];
  list.sort((a, b) => (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b)));
  return list.map((p) => PLATFORM_NAMES[p] || p.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())).join(", ");
}

export const isVideoAd = (ad: Pick<CompetitorAd, "media_type" | "video_url">) => /video/i.test(ad.media_type || "") || Boolean(ad.video_url);

/**
 * Ads with the same words, button, destination and format, as one card. A big advertiser runs one
 * message as dozens of ads (1-800-GOT-JUNK: 40 ads read, a handful of messages), and forty identical
 * cards hide the few that differ. An ad with no words is never grouped: its creative is the message.
 * The first ad of each group is the one the list put first (longest running, or newest).
 */
export type AdGroup = { key: string; lead: CompetitorAd; ads: CompetitorAd[] };
export function groupAds(ads: CompetitorAd[]): AdGroup[] {
  const groups = new Map<string, AdGroup>();
  for (const ad of ads) {
    const words = [displayCopy(ad.headline), displayCopy(ad.primary_text)].filter(Boolean).join("|");
    const key = words
      ? [words, ad.cta || "", landingKey(ad.link_url) || funnelKind(ad.link_url, ad.cta_type), isVideoAd(ad) ? "video" : "image"].join("|").toLowerCase()
      : `ad:${ad.id}`;
    const g = groups.get(key);
    if (g) g.ads.push(ad);
    else groups.set(key, { key, lead: ad, ads: [ad] });
  }
  return [...groups.values()];
}

/* ── money ──────────────────────────────────────────────────────────────── */

export function usd(value: number): string {
  if (!Number.isFinite(value)) return "$0.00";
  return `$${value < 0.01 && value > 0 ? value.toFixed(3) : value.toFixed(2)}`;
}

/**
 * What scanning `count` competitors costs, for the Scan all button and confirm.
 * Scrape Creators: one credit per request, a page is one or two requests (a typed name adds one).
 * Apify: $0.75 per 1,000 ads.
 */
export function scanCostLine(count: number, cost: Pick<ScanCost, "provider" | "per_scan_usd">): string {
  if (cost.provider === "scrapecreators") return count > 1 ? `About 1 to 2 Scrape Creators credits each, up to ${count * 2} in all` : "About 1 to 2 Scrape Creators credits";
  const each = `About ${usd(cost.per_scan_usd)} each`;
  return count > 1 ? `${each}, ${usd(cost.per_scan_usd * count)} in all` : each;
}

/** The short price on a Scan now button: "1 to 2 credits" or "about $0.05". */
export function perScanText(cost: Pick<ScanCost, "provider" | "per_scan_usd" | "per_scan_text">): string {
  return cost.per_scan_text || `about ${usd(cost.per_scan_usd)}`;
}

/** The footnote's provider line: who reads the Ad Library and what it costs. */
export function providerNote(provider: ScanProvider | null, cost: ScanCost): string | null {
  if (!provider) return null;
  if (provider.id === "scrapecreators") {
    const left = cost.credits_left === null || cost.credits_left === undefined ? "" : `, ${cost.credits_left.toLocaleString("en-US")} credits left`;
    return `Read through Scrape Creators: about 1 to 2 credits per competitor scan${left}.`;
  }
  return `Read through Apify: about ${usd(cost.per_scan_usd)} per competitor scan ($${cost.per_1000_ads_usd.toFixed(2)} per 1,000 ads).`;
}

/** 216317 → "216K", 3949 → "3,949". */
export function compactCount(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  if (n < 10000) return n.toLocaleString("en-US");
  if (n < 1000000) return `${Math.round(n / 1000)}K`;
  return `${(n / 1000000).toFixed(n < 10000000 ? 1 : 0)}M`;
}

/** What the Active ads cell shows under the number, if anything. */
export function activeNote(stats: CompetitorStats | null): string | null {
  if (!stats) return null;
  return stats.active_from_meta && stats.active > stats.scanned ? `${stats.scanned} read` : null;
}
